'use client';

import React, { FC, useEffect, useState } from 'react';

import classNames from 'classnames';

import { BlockImage, UniversalLink } from '@/components/common';
import { DynamicComponent } from '@/components/common/DynamicComponent';
import { Image } from '@/components/ui';

import useLocale from '@/hooks/useLocale';

import { Link, usePathname } from '@/navigation';

import { buildLinkUrl } from '@/utils/linkBuilder';

import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  NAV_EVENT_TYPES,
  PAGE_LOCATIONS,
  trackElementPresented,
  trackItemClickedEvent,
} from '@/utils/rudderstack';

import generateTranslationsObject from '@/utils/translations/generateTranslationObject';

import { Cart } from '../Cart/Cart';
import { GenericAccordion } from '../GenericAccordion/GenericAccordion';
import { NavComponentProps } from '../GlobalNav';

import UserMenu from './UserMenu/UserMenu';

const TRANSITION_DURATION = 'duration-700';

export const SmallNav: FC<NavComponentProps> = ({
  logo,
  groups,
  groupsMobile,
  drawerDirection = 'left',
  signInLink,
  bannerLinks,
  linkMenuLoggedIn,
  i18n,
  smallDivider,
  isSticky,
  buttonCta,
  hasLargeWordsStyle,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [isMenuClosing, setIsMenuClosing] = useState(false);

  const translations = generateTranslationsObject(i18n?.fields);

  const pathname = usePathname();

  const locale = useLocale();

  const handleMenuOpen = (isOpen: boolean) => {
    document.body.classList.add('overflow-hidden');

    setIsMenuOpen(isOpen);

    trackElementPresented({
      name: NAV_EVENT_TYPES.PRIMARY_NAV_EXPANDED,
    });
  };

  const handleMenuClose = () => {
    setIsMenuClosing(true);

    setTimeout(() => {
      document.body.classList.remove('overflow-hidden');

      setIsMenuOpen(false);

      setIsMenuClosing(false);
    }, 200); // Match animation duration
  };

  // Close menu when route changes
  useEffect(() => {
    if (isMenuOpen) {
      handleMenuClose();
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const shouldShowCta = buttonCta && isSticky;

  return (
    <>
      {/* Nav bar */}
      <nav
        data-testid="small-global-nav"
        className={classNames(
          `bg-white lg:hidden block px-5 sticky top-0 z-50 transition-shadow ${TRANSITION_DURATION}`,
          {
            'shadow-lg': isSticky,
          },
        )}
      >
        <div
          className={classNames(
            'mx-auto h-[64px] grid grid-cols-[24px_auto_32px] gap-x-4 items-center',
            {
              'grid-cols-[24px_auto_auto]': shouldShowCta && hasLargeWordsStyle,
            },
          )}
        >
          {/* Menu button */}
          <button aria-label="Menu" onClick={() => handleMenuOpen(!isMenuOpen)}>
            <svg
              width="24"
              height="22"
              viewBox="0 0 24 22"
              className="text-charcoal"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="0" y="0" width="24" height="2" fill="currentColor" />
              <rect x="0" y="9" width="24" height="2" fill="currentColor" />
              <rect x="0" y="20" width="24" height="2" fill="currentColor" />
            </svg>
          </button>

          {/* Logo */}
          {logo && (
            <div className="relative">
              <Link
                href="/"
                className={classNames(
                  `block w-max transition-all ${TRANSITION_DURATION}`,
                  {
                    'ml-0 translate-x-0': shouldShowCta,
                    'ml-[50%] translate-x-[-50%]': !shouldShowCta,
                  },
                )}
                data-testid="lovevery-logo-mobile"
                onClick={() => {
                  trackItemClickedEvent({
                    url: window.location.href,
                    type: CLICKED_ITEM_TYPES.IMAGE,
                    pageLocation: PAGE_LOCATIONS.NAVIGATION,
                    cta: CTA_NAMES.LOVEVERY_LOGO,
                  });
                }}
              >
                <Image
                  imageData={logo}
                  alt={logo.fields.altText}
                  className={classNames(
                    `transition-width ${TRANSITION_DURATION}`,
                    {
                      'w-[112px] md:w-[164px]': shouldShowCta,
                      'w-[164px]': !shouldShowCta,
                    },
                  )}
                />
              </Link>
            </div>
          )}

          <div className="relative flex items-center">
            {/* Button CTA */}
            {buttonCta && (
              <div
                className={classNames(
                  `absolute right-0 flex items-center justify-end transition-opacity ${TRANSITION_DURATION}`,
                  {
                    'opacity-100': shouldShowCta,
                    'opacity-0 pointer-events-none': !shouldShowCta,
                  },
                )}
                data-testid="small-nav-button-cta"
              >
                <UniversalLink
                  classes={classNames(
                    'btn bg-blue-deep border-blue-deep hover:bg-[#131C66] hover:border-[#131C66] active:bg-[#131C66] active:border-[#131C66] text-sm px-4 min-h-8',
                    {
                      'w-full': hasLargeWordsStyle,
                      'h-8': !hasLargeWordsStyle,
                    },
                  )}
                  link={buttonCta.link}
                  onClick={() => {
                    trackItemClickedEvent({
                      type: CLICKED_ITEM_TYPES.BUTTON,
                      cta: buttonCta.link?.fields.linkText as string,
                      pageLocation: buttonCta.pageLocation,
                      url: window.location.href,
                    });
                  }}
                />
              </div>
            )}

            {/* Cart */}
            <Cart
              className={classNames(
                `transition-opacity ${TRANSITION_DURATION}`,
                {
                  'opacity-0 pointer-events-none': shouldShowCta,
                },
              )}
            />
          </div>
        </div>
      </nav>

      {/* Menu */}
      {isMenuOpen && (
        <div
          data-testid="global-nav-mobile-content"
          className={`
            pb-8 bg-white border-t border-grey-mid-light lg:hidden fixed top-0 z-[10001]
            bottom-0 w-full transform transition-all duration-200 ease-in-out max-h-[100vh] overflow-y-auto
          ${
            drawerDirection === 'left'
              ? `left-0 ${isMenuClosing ? 'animate-slide-out-left' : 'animate-slide-in-left'}`
              : `right-0 ${isMenuClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`
          }`}
        >
          <div className="flex justify-end pb-4">
            <button
              aria-label="Close"
              onClick={handleMenuClose}
              className="p-5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
              >
                <path
                  d="M11 12.827L2.20416 21.6233C1.96402 21.8632 1.66219 21.986 1.29866 21.9918C0.935414 21.9973 0.628088 21.8744 0.37668 21.6233C0.12556 21.3719 0 21.0673 0 20.7096C0 20.3518 0.12556 20.0472 0.37668 19.7958L9.17295 11L0.37668 2.20416C0.13683 1.96402 0.0140154 1.66219 0.00823591 1.29866C0.00274537 0.935415 0.12556 0.628088 0.37668 0.37668C0.628088 0.12556 0.932669 0 1.29042 0C1.64817 0 1.95275 0.12556 2.20416 0.37668L11 9.17295L19.7958 0.37668C20.036 0.13683 20.3378 0.0140154 20.7013 0.00823591C21.0646 0.00274537 21.3719 0.12556 21.6233 0.37668C21.8744 0.628088 22 0.932669 22 1.29042C22 1.64817 21.8744 1.95275 21.6233 2.20416L12.827 11L21.6233 19.7958C21.8632 20.036 21.986 20.3378 21.9918 20.7013C21.9973 21.0646 21.8744 21.3719 21.6233 21.6233C21.3719 21.8744 21.0673 22 20.7096 22C20.3518 22 20.0472 21.8744 19.7958 21.6233L11 12.827Z"
                  fill="#424140"
                />
              </svg>
            </button>
          </div>

          <div>
            {(groupsMobile ?? groups)?.map((group) => (
              <GenericAccordion
                key={group.sys.id}
                className="border-b border-transparent last:border-b-0 px-4"
                header={<span>{group.fields.title}</span>}
                headerClassName="w-full py-3 text-left flex items-center justify-between text-lg text-charcoal pr-1.5"
                transitionDuration={300}
                testId={`${group.sys.id}-accordion`}
                showChevron={true}
                onToggle={(isOpen) => {
                  const shouldTrackElementPresented =
                    isOpen && group.fields.title;

                  shouldTrackElementPresented &&
                    trackElementPresented({
                      name: NAV_EVENT_TYPES.SECONDARY_NAV_EXPANDED,
                      title: group?.fields.title as string,
                    });
                }}
              >
                <div className="pl-[21px] flex flex-col pb-4 lg:pb-0">
                  {group.fields.columns?.map((column) =>
                    column.fields.items?.map((item) => (
                      <DynamicComponent
                        component={item}
                        key={item.sys.id}
                        onClose={handleMenuClose}
                      />
                    )),
                  )}
                </div>
              </GenericAccordion>
            ))}

            <div className="my-6">
              {smallDivider && <BlockImage {...smallDivider.fields} />}
            </div>

            <UserMenu
              signInLink={signInLink}
              linkMenuLoggedIn={linkMenuLoggedIn}
              translations={translations}
              onClose={handleMenuClose}
            />

            <div className="border-b border-transparent last:border-b-0 px-4">
              {bannerLinks && (
                <div className="gap-4 flex flex-col">
                  {bannerLinks.map((link) => {
                    const href = buildLinkUrl(link, locale);

                    return (
                      <Link
                        key={link.sys.id}
                        href={href}
                        onClick={() => {
                          trackItemClickedEvent({
                            url: window.location.href,
                            type: CLICKED_ITEM_TYPES.LINK,
                            pageLocation: PAGE_LOCATIONS.NAVIGATION,
                            cta: link.fields.linkText || '',
                          });

                          handleMenuClose();
                        }}
                      >
                        {(link.fields as any).linkText || 'Link'}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(SmallNav);
